import Language from '../../../../language/Language';
import { AddressModeOptions, CardInputProps, LayoutObj } from './types';
import {
    CREDIT_CARD,
    CREDIT_CARD_NAME_BOTTOM,
    CREDIT_CARD_NAME_TOP,
    KCP_CARD,
    KCP_CARD_NAME_BOTTOM,
    KCP_CARD_NAME_TOP,
    SSN_CARD,
    SSN_CARD_NAME_BOTTOM,
    SSN_CARD_NAME_TOP
} from './layouts';
import { AddressSpecifications, StringObject } from '../../../internal/Address/types';
import { PARTIAL_ADDRESS_SCHEMA } from '../../../internal/Address/constants';
import type { InstallmentOptions, InstallmentsState } from './components/Installments/Installments';
import { SFPProps } from '../../../internal/SecuredFields/SFP/types';
import { BRAND_READABLE_NAME_MAP } from '../../../internal/SecuredFields/lib/constants';
import useImage, { UseImageHookType } from '../../../../core/Context/useImage';
import { SF_ErrorCodes } from '../../../../core/Errors/constants';
import { BrandObject, CardBrandsConfiguration, DualBrandSelectElement, DualBrandButtons } from '../../types';
import { PaymentAmount } from '../../../../types';

export const getCardImageUrl = (brand: string, getImage: UseImageHookType): string => {
    const imageOptions = {
        type: brand === 'card' ? 'nocard' : brand || 'nocard',
        extension: 'svg'
    };

    return getImage(imageOptions)(brand);
};

/**
 * 'installments' should be added to the payload if it has plan as revolving or bonus
 * or value greater than 1.
 *
 * More about it here: COWEB-1070
 */
export const shouldIncludeInstallmentsInPaymentData = (installment?: InstallmentsState): boolean => {
    return ['revolving', 'bonus'].includes(installment?.plan) || installment?.value > 1;
};

export const getLayout = ({
    props,
    showKCP,
    showBrazilianSSN,
    countrySpecificSchemas = null,
    billingAddressRequiredFields = null
}: LayoutObj): string[] => {
    let layout = CREDIT_CARD;
    const hasRequiredHolderName = props.hasHolderName && props.holderNameRequired;

    if (hasRequiredHolderName) {
        layout = props.positionHolderNameOnTop ? CREDIT_CARD_NAME_TOP : CREDIT_CARD_NAME_BOTTOM;
    }

    if (showKCP) {
        layout = KCP_CARD;
        if (hasRequiredHolderName) {
            layout = props.positionHolderNameOnTop ? KCP_CARD_NAME_TOP : KCP_CARD_NAME_BOTTOM;
        }
    }

    if (showBrazilianSSN) {
        layout = SSN_CARD;
        if (hasRequiredHolderName) {
            layout = props.positionHolderNameOnTop ? SSN_CARD_NAME_TOP : SSN_CARD_NAME_BOTTOM;
        }
    }

    // w. Billing address
    if (countrySpecificSchemas) {
        // Flatten array and remove any numbers that describe how fields should be aligned
        const countrySpecificSchemasFlat: string[] = countrySpecificSchemas['flat'](2).filter(item => typeof item !== 'number') as string[];

        let countryBasedAddressLayout = countrySpecificSchemasFlat;

        if (billingAddressRequiredFields) {
            // Get intersection of the 2 arrays
            countryBasedAddressLayout = countrySpecificSchemasFlat.filter(x => billingAddressRequiredFields.includes(x));
        }

        layout = CREDIT_CARD.concat(countryBasedAddressLayout);
        if (hasRequiredHolderName) {
            layout = props.positionHolderNameOnTop
                ? CREDIT_CARD_NAME_TOP.concat(countryBasedAddressLayout)
                : CREDIT_CARD_NAME_BOTTOM.concat(countryBasedAddressLayout);
        }
        // TODO we are not yet creating a layout for AVS + SSN field (w. or w/o holderName) - is AVS + SSN a real world scenario?
    }
    return layout;
};

/**
 * Lookup service to map local (CardInput) field refs to a key, possibly region specific, by which to retrieve the translation
 */
export const mapFieldKey = (key: string, i18n: Language, countrySpecificLabels: StringObject): string => {
    // console.log('### utils::mapFieldKey:: key', key);
    switch (key) {
        case 'socialSecurityNumber':
            return i18n.get(`boleto.${key}`);
        // Address related - if we have a country specific key for the field - use that to get the translation
        case 'street':
        case 'houseNumberOrName':
        case 'postalCode':
        case 'stateOrProvince':
        case 'city':
        case 'country':
            return countrySpecificLabels?.[key] ? i18n.get(countrySpecificLabels?.[key]) : i18n.get(key);
        // We know that the translated error messages do contain a reference to the field they refer to, so we won't need to map them (currently applies mostly to SecuredFields related errors)
        default:
            return null;
    }
};

export const extractPropsForCardFields = (props: CardInputProps) => {
    return {
        // Extract props for CardFieldsWrapper & StoredCardFieldsWrapper(just needs hasCVC, installmentOptions)
        billingAddressRequired: props.billingAddressRequired,
        billingAddressRequiredFields: props.billingAddressRequiredFields,
        billingAddressAllowedCountries: props.billingAddressAllowedCountries,
        brandsConfiguration: props.brandsConfiguration,
        showStoreDetailsCheckbox: props.showStoreDetailsCheckbox,
        hasCVC: props.hasCVC,
        hasHolderName: props.hasHolderName,
        holderNameRequired: props.holderNameRequired,
        installmentOptions: props.installmentOptions,
        placeholders: props.placeholders,
        positionHolderNameOnTop: props.positionHolderNameOnTop,
        // Extract props for CardFields > CardNumber
        showBrandIcon: props.showBrandIcon,
        showContextualElement: props.showContextualElement,
        // Extract props for StoredCardFields
        lastFour: props.lastFour,
        expiryMonth: props.expiryMonth,
        expiryYear: props.expiryYear,
        disclaimerMessage: props.disclaimerMessage
    };
};

export const extractPropsForSFP = (props: CardInputProps): Pick<SFPProps, 'clientKey' | 'loadingContext'> & Partial<SFPProps> => {
    return {
        autoFocus: props.autoFocus,
        brands: props.brands,
        brandsConfiguration: props.brandsConfiguration,
        clientKey: props.clientKey,
        countryCode: props.countryCode,
        forceCompat: props.forceCompat,
        i18n: props.i18n,
        implementationType: props.implementationType,
        keypadFix: props.keypadFix,
        legacyInputMode: props.legacyInputMode,
        loadingContext: props.loadingContext,
        maskSecurityCode: props.maskSecurityCode,
        exposeExpiryDate: props.exposeExpiryDate,
        minimumExpiryDate: props.minimumExpiryDate,
        onAdditionalSFConfig: props.onAdditionalSFConfig,
        onAdditionalSFRemoved: props.onAdditionalSFRemoved,
        onAllValid: props.onAllValid,
        onAutoComplete: props.onAutoComplete,
        onBinValue: props.onBinValue,
        onConfigSuccess: props.onConfigSuccess,
        handleKeyPress: props.handleKeyPress,
        onError: props.onError,
        onFieldValid: props.onFieldValid,
        onLoad: props.onLoad,
        placeholders: props.placeholders,
        resources: props.resources,
        showContextualElement: props.showContextualElement,
        showWarnings: props.showWarnings,
        trimTrailingSeparator: props.trimTrailingSeparator
    };
};

export const handlePartialAddressMode = (addressMode: AddressModeOptions): AddressSpecifications | null => {
    return addressMode == AddressModeOptions.partial ? PARTIAL_ADDRESS_SCHEMA : null;
};

// Almost all errors are blur based, but some SF ones are not i.e. when an unsupported card is entered or the expiry date is out of range
export function lookupBlurBasedErrors(errorCode) {
    // If it's NOT one of these, then it's a blur based error
    return ![
        SF_ErrorCodes.ERROR_MSG_UNSUPPORTED_CARD_ENTERED,
        SF_ErrorCodes.ERROR_MSG_CARD_TOO_OLD,
        SF_ErrorCodes.ERROR_MSG_CARD_TOO_FAR_IN_FUTURE,
        SF_ErrorCodes.ERROR_MSG_CARD_EXPIRES_TOO_SOON
    ].includes(errorCode);
}

export function getFullBrandName(brand: string): string {
    return BRAND_READABLE_NAME_MAP[brand] ?? brand;
}

export const mapDualBrandButtons = (
    dualBrandSelectElements: DualBrandSelectElement[],
    brandsConfiguration?: CardBrandsConfiguration
): DualBrandButtons[] => {
    return dualBrandSelectElements.map(item => {
        const brand = item.id;
        const getImage = useImage();
        const imageName = brand === 'card' ? 'nocard' : brand;
        const imageURL = brandsConfiguration?.[brand]?.icon ?? getCardImageUrl(imageName, getImage);

        // TODO - check below if we have to still generate altName through the mapping function or whether it just
        //  corresponds to item.brandObject.localeBrand
        return {
            id: item.id,
            name: item.brandObject.localeBrand || item.brandObject.brand,
            imageURL,
            altName: getFullBrandName(brand)
        };
    });
};

/**
 *  Checks if any of the brands requiring a selection mechanism are present in the binLookup response.
 *
 * If the result from Array.some is true - then we are in a dual branding scenario that requires selection, i.e.
 * - Show the dualBranding selector UI
 * - Preselect a card brand
 */
export const requiresDualBrandSelection = (
    brandsRequiringSelection: readonly string[],
    returnedDualBrandingObjects: DualBrandSelectElement[] | BrandObject[],
    key: string
) => returnedDualBrandingObjects.some(item => brandsRequiringSelection.includes(item[key]));

/**
 * Determines whether the Installments component should be rendered.
 *
 * Installments Component is displayed when:
 * - installmentOptions is provided and not empty
 * - amount is provided and not zero
 * - fundingSource is not provided or is 'credit'
 *
 * @param params.installmentOptions - The installment configuration options
 * @param params.fundingSource - The card funding source (e.g. 'credit', 'debit')
 * @param params.amount - The payment amount
 * @returns Whether the Installments component should be rendered
 */
export const shouldShowInstallmentsComponent = ({
    installmentOptions,
    fundingSource,
    amount
}: {
    installmentOptions?: InstallmentOptions;
    fundingSource?: string;
    amount?: PaymentAmount;
}): boolean => {
    if (!installmentOptions || Object.keys(installmentOptions).length === 0) return false;
    if (!amount || amount.value === 0) return false;
    if (fundingSource && fundingSource !== 'credit') return false;

    return true;
};
